import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * useBrainDefaults — fetch the XEA-Brain smart defaults for a realm.
 *
 * Returns `{ defaults, loading, refresh }` where `defaults` is an object
 * mapping signal_name → top picked value (e.g. {style: "cinematic"}).
 *
 * The hook fetches once on mount per realm/token combo and exposes a
 * `refresh()` callback for refetching after generation. Empty result `{}`
 * is the safe default — the consumer applies a key only when the field
 * has not been touched yet.
 */
export function useBrainDefaults(realm) {
  const { token } = useAuth();
  const [defaults, setDefaults] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchDefaults = useCallback(async () => {
    if (!token || !realm) return;
    try {
      const r = await axios.get(`${API}/brain/defaults/${realm}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDefaults(r.data?.defaults || {});
    } catch (_e) {
      // Brain is best-effort — silent on failures so it never blocks UX.
      setDefaults({});
    } finally {
      setLoading(false);
    }
  }, [token, realm]);

  useEffect(() => {
    fetchDefaults();
  }, [fetchDefaults]);

  return { defaults, loading, refresh: fetchDefaults };
}

/**
 * Log a finished generation's signals through the brain. Fire-and-forget.
 * Frontend pages call this when the user successfully submits a gen so
 * the brain learns from what they actually shipped — not just what
 * pre-filled defaults said.
 */
export async function logBrainEvent(token, realm, signals) {
  if (!token || !realm) return;
  try {
    await axios.post(
      `${API}/brain/log`,
      { realm, signals: signals || {} },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  } catch (_e) {
    /* silent — never block the user */
  }
}
